import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useExpenses, useExpenseCategories, useExport } from '@/hooks/useApi';
import ExpenseScanModal from './ExpenseScanModal';
import PageSkeleton from '@/components/PageSkeleton';
import Pagination from '@/components/Pagination';

const STATUS_COLORS: Record<string, string> = {
  pending: 'badge-yellow', approved: 'badge-blue', paid: 'badge-green', rejected: 'badge-red', cancelled: 'badge-gray',
};

const ROW_COLORS: Record<string, string> = {
  pending: 'row-amber',
  approved: 'row-blue',
  paid: 'row-green',
  rejected: 'row-red',
  cancelled: 'row-gray',
};

export default function ExpensesPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [scanOpen, setScanOpen] = useState(false);
  const statusFilter = searchParams.get('status') || '';
  const categoryFilter = searchParams.get('category') || '';
  const page = Number(searchParams.get('page')) || 1;

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedSearch = searchParams.get('search') || '';

  const { data, isLoading } = useExpenses({ page, status: statusFilter, category: categoryFilter, search: debouncedSearch });
  const { data: categories } = useExpenseCategories();
  const exportMutation = useExport();

  function setFilter(key: string, value: string) {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  }

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilter('search', value);
    }, 300);
  };

  function setPage(p: number) {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
  }

  if (isLoading) return <PageSkeleton />;

  return (
    <div>
      <div className="page-header">
        <h1 className="text-2xl font-bold">{t('expenses.title')}</h1>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => exportMutation.mutate({ type: 'expenses' })}>
            {t('common.export')}
          </button>
          <button className="btn btn-secondary" onClick={() => setScanOpen(true)}>
            📷 {t('expenses.scan.button')}
          </button>
          <Link to="/expenses/new" className="btn btn-primary">
            + {t('expenses.addExpense')}
          </Link>
        </div>
      </div>

      <ExpenseScanModal open={scanOpen} onClose={() => setScanOpen(false)} />

      <div className="card card-body mb-4 flex flex-col sm:flex-row gap-3">
        <input
          type="text" placeholder={t('common.search') + '...'} className="input flex-1"
          value={search} onChange={(e) => handleSearchChange(e.target.value)}
        />
        <select className="input w-auto" value={statusFilter} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">{t('common.allStatuses')}</option>
          {['pending', 'approved', 'paid', 'rejected', 'cancelled'].map((s) => (
            <option key={s} value={s}>{t(`expenses.status.${s}`)}</option>
          ))}
        </select>
        <select className="input w-auto" value={categoryFilter} onChange={(e) => setFilter('category', e.target.value)}>
          <option value="">{t('expenses.allCategories')}</option>
          {categories?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="card overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t('expenses.date')}</th>
              <th>{t('expenses.category')}</th>
              <th>{t('expenses.description')}</th>
              <th>{t('expenses.supplier')}</th>
              <th className="text-right">{t('common.amount')}</th>
              <th>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 6 }).map((_, j) => <td key={j}><div className="skeleton h-4 w-20" /></td>)}</tr>
            ))}
            {data?.data?.map((exp) => (
              <tr key={exp.id} className={ROW_COLORS[exp.status] || ''}>
                <td className="text-gray-500">{new Date(exp.expenseDate).toLocaleDateString()}</td>
                <td>
                  {exp.category?.name && (
                    <span className="inline-flex items-center gap-1 text-sm">
                      {exp.category?.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: exp.category.color }} />}
                      {exp.category.name}
                    </span>
                  )}
                </td>
                <td className="text-gray-900 font-medium">{exp.description}</td>
                <td className="text-gray-600">{exp.supplier?.name || '—'}</td>
                <td className="text-right font-medium">€{Number(exp.totalAmount).toLocaleString('de-BE', { minimumFractionDigits: 2 })}</td>
                <td><span className={`badge ${STATUS_COLORS[exp.status] || 'badge-gray'}`}>{t(`expenses.status.${exp.status}`)}</span></td>
              </tr>
            ))}
            {!isLoading && (!data?.data || data.data.length === 0) && (
              <tr><td colSpan={6} className="text-center text-gray-400 py-12">{t('common.noResults')}</td></tr>
            )}
          </tbody>
        </table>
        <Pagination meta={data?.meta} onPageChange={setPage} />
      </div>
    </div>
  );
}
